import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { activateAccount, resendActivation } from "../../api/FlaskAPI";
import { validateEmail } from "../../utils/validations";
import Loading from "../../components/Buttons/Loading";
import "./styles.css";


const ShieldIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3z"
            stroke="var(--primary-dark, #355e7e)" strokeWidth="1.5" fill="none" />
        <path d="M9 12l2 2 4-4" stroke="var(--primary-dark, #355e7e)" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const SuccessIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="#4caf50" />
        <path d="M8 12.5l2.5 2.5L16 9.5" stroke="white" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ErrorIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="#f44336" />
        <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);


function AccountActivation() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { token } = useParams();

    // Main status: activating | success | error
    const [status, setStatus] = useState("activating");
    const [errorMessage, setErrorMessage] = useState("");

    // Resend form state
    const [showResendForm, setShowResendForm] = useState(false);
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [resendLoading, setResendLoading] = useState(false);

    useEffect(() => {
        if (token) {
            handleActivation(token);
        } else {
            setStatus("error");
            setErrorMessage(t("Sign.invalid_token"));
        }
    }, []);

    const handleActivation = async (token) => {
        try {
            const res = await activateAccount(token);

            if (res?.status === 200) {
                setStatus("success");
                const message =
                    i18n.language === "ar" ? res.data?.message_ar : res.data?.message;
                toast.success(message || t("Sign.activation_success"));
                setTimeout(() => navigate("/"), 3000);
            } else {
                handleError(res);
            }
        } catch (error) {
            handleError(error.response);
        }
    };

    const handleError = (res) => {
        setStatus("error");
        const message =
            i18n.language === "ar" ? res?.data?.message_ar : res?.data?.message;
        setErrorMessage(message || t("Sign.activation_failed"));
    };

    const handleResendActivation = async () => {
        if (!email) {
            setEmailError(t("Sign.email_required"));
            return;
        }
        if (!validateEmail(email)) {
            setEmailError(t("Sign.email_not_valid"));
            return;
        }

        setEmailError("");
        setResendLoading(true);

        try {
            const res = await resendActivation({ email });

            if (res?.status === 200) {
                const message =
                    i18n.language === "ar" ? res.data?.message_ar : res.data?.message;
                toast.success(message || t("Sign.activation_email_resent"));
                setShowResendForm(false);
                setEmail("");
            } else {
                const errorMsg =
                    i18n.language === "ar" ? res?.data?.message_ar : res?.data?.message;
                toast.error(errorMsg || t("request_error"));
            }
        } catch (error) {
            const errorMsg =
                i18n.language === "ar"
                    ? error.response?.data?.message_ar
                    : error.response?.data?.message;
            toast.error(errorMsg || t("request_error"));
        } finally {
            setResendLoading(false);
        }
    };

    const onEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);

        if (!value) {
            setEmailError(t("Sign.email_required"));
        } else if (!validateEmail(value)) {
            setEmailError(t("Sign.email_not_valid"));
        } else {
            setEmailError("");
        }
    };

    return (
        <div className="activation-page">
            <div className="activation-card">
                {/* ACTIVATING STATE */}
                {status === "activating" && (
                    <>
                        <div className="activation-icon">
                            <ShieldIcon />
                        </div>
                        <h2 className="activation-title">{t("Sign.activating_account")}</h2>
                        <Loading />
                    </>
                )}

                {/* SUCCESS STATE */}
                {status === "success" && (
                    <>
                        <div className="status-badge">
                            <SuccessIcon />
                        </div>
                        <h2 className="activation-title">{t("Sign.activation_success")}</h2>
                        <p className="activation-subtitle">{t("Sign.redirecting_to_login")}</p>
                    </>
                )}

                {/* ERROR STATE */}
                {status === "error" && (
                    <>
                        <div className="status-badge">
                            <ErrorIcon />
                        </div>
                        <h2 className="activation-title">{t("Sign.activation_failed")}</h2>
                        <p className="activation-subtitle error-text">{errorMessage}</p>

                        {!showResendForm ? (
                            <div className="activation-actions">
                                <button
                                    className="activation-btn primary"
                                    onClick={() => setShowResendForm(true)}
                                >
                                    {t("Sign.resend_activation")}
                                </button>
                                <a className="back-link" onClick={() => navigate("/")}>
                                    {t("Sign.return")}
                                </a>
                            </div>
                        ) : (
                            <div className="resend-form">
                                <p className="activation-subtitle">{t("Sign.enter_email_activation")}</p>

                                <div className="activation-field">
                                    <input
                                        type="email"
                                        name="email"
                                        value={email}
                                        onChange={onEmailChange}
                                        placeholder={t("Sign.email")}
                                        className={`activation-input ${emailError ? "input-error" : ""}`}
                                    />
                                    {emailError && (
                                        <span className="field-error">{emailError}</span>
                                    )}
                                </div>

                                <div className="activation-actions">
                                    {resendLoading ? (
                                        <Loading />
                                    ) : (
                                        <>
                                            <button
                                                className="activation-btn primary"
                                                onClick={handleResendActivation}
                                            >
                                                {t("Sign.resend")}
                                            </button>
                                            <a
                                                className="back-link"
                                                onClick={() => {
                                                    setShowResendForm(false);
                                                    setEmail("");
                                                    setEmailError("");
                                                }}
                                            >
                                                {t("Sign.cancel")}
                                            </a>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default AccountActivation;
